import { useState, type ReactNode } from 'react';
import { Settings2, Tag as TagIcon } from 'lucide-react';
import { Button, Card, Fact, Facts, MarketName, PlatformName, TagChip } from '@/components/ui';
import { AvailabilityCard } from '@/components/sites/AvailabilityCard';
import { LaunchCard } from '@/components/geo/LaunchCard';
import { ImportCard } from '@/components/sites/ImportCard';
import { PlatformCard } from '@/components/sites/PlatformCard';
import { PreviewCard } from '@/components/sites/PreviewCard';
import { RemnantsCard } from '@/components/sites/RemnantsCard';
import { RuntimeCard } from '@/components/sites/RuntimeCard';
import { ServerLink } from '@/components/sites/SitesTable';
import { SiteTagsDialog } from '@/components/sites/SiteTagsDialog';
import { formatDateTime, formatRelative } from '@/utils/format';
import { BACKUP_POLICY_LABELS, label, sitePlatform } from '@/utils/labels';
import type { SiteCapabilities } from '@/utils/siteCapabilities';
import type { SiteDetail } from '@/types/api';

/** A site's configuration, and the cards for what is going on with it now. */
export function OverviewTab({ site, may }: { readonly site: SiteDetail; readonly may: SiteCapabilities }): ReactNode {
  const [tagging, setTagging] = useState(false);

  return (
    <div className="stack stack-loose">
      {site.launchPending && site.status === 'ready' ? <LaunchCard site={site} mayLaunch={may.launch} /> : null}
      <PreviewCard site={site} mayRefresh={may.editPlatform} />

      {/* One card, not a column of them: these are all properties of the same
          thing, and tags belong beside them rather than in a card of their own
          whose only content is a button. */}
      <Card title="Конфигурация" icon={Settings2}>
        <Facts>
          <Fact label="Теги">
            <div className="row-tight row-wrap">
              {site.tags.length === 0 ? (
                <span className="faint">нет</span>
              ) : (
                site.tags.map((tag) => <TagChip key={tag.id} tag={tag} size="sm" />)
              )}
              {may.tag ? (
                <Button size="sm" variant="ghost" icon={TagIcon} onClick={() => setTagging(true)}>
                  Изменить
                </Button>
              ) : null}
            </div>
          </Fact>
          <Fact label="Идентификатор">
            <span className="mono">{site.slug}</span>
          </Fact>
          <Fact label="Платформа">
            <PlatformName platform={sitePlatform(site).key} label={sitePlatform(site).label} />
            {site.engineVersion ? <span className="muted"> · {site.engineVersion}</span> : null}
          </Fact>
          <Fact label="Шаблон">
            {site.templateName ? (
              <>
                {site.templateName} <span className="mono muted">{site.templateVersion}</span>
              </>
            ) : (
              '—'
            )}
          </Fact>
          <Fact label="Сервер">
            <ServerLink site={site} empty="не размещён" />
          </Fact>
          <Fact label="Профиль">{site.runtimeProfile}</Fact>
          <Fact label="Часовой пояс">{site.timezone}</Fact>
          {site.country || site.language ? (
            <Fact label="Страна и язык">
              <MarketName country={site.country} language={site.language} />
            </Fact>
          ) : null}
          <Fact label="Политика копий">{site.backupPolicy ? label(BACKUP_POLICY_LABELS, site.backupPolicy) : 'не задана'}</Fact>
          <Fact label="Проверен">{site.healthCheckedAt ? formatRelative(site.healthCheckedAt) : 'ни разу'}</Fact>
          <Fact label="Создан">{formatDateTime(site.createdAt)}</Fact>
        </Facts>
      </Card>

      <PlatformCard site={site} mayEdit={may.editPlatform} mayUseCredentials={may.credentials} />

      <ImportCard site={site} mayManage={may.manageImport} />

      <AvailabilityCard site={site} />

      {site.runtime ? <RuntimeCard runtime={site.runtime} /> : null}

      <RemnantsCard site={site} mayRemove={may.removeRemnants} />

      <SiteTagsDialog open={tagging} site={site} onClose={() => setTagging(false)} />
    </div>
  );
}
